import { format, isSameMonth, startOfWeek, subWeeks } from "date-fns";
import { enUS, es } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  X,
  XCircle,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { FaBriefcaseMedical } from "react-icons/fa";
import { LuFileClock } from "react-icons/lu";

interface PTORequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMonth: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  monthMatrix: Date[][];
  selection: Record<string, string>;
  onToggle: (dateStr: string) => void;
  blockedMap: Record<string, { AM: boolean; PM: boolean; FULL: boolean }>;
  description: string;
  onDescriptionChange: (value: string) => void;
  submitError: string | null;
  onFinalize: () => void;
  isPending: boolean;
  showSuccessAnim: boolean;
  requestType: string;
  userName?: string;
}

export function PTORequestModal({
  isOpen,
  onClose,
  currentMonth,
  onPrevMonth,
  onNextMonth,
  monthMatrix,
  selection,
  onToggle,
  blockedMap,
  description,
  onDescriptionChange,
  submitError,
  onFinalize,
  isPending,
  showSuccessAnim,
  requestType,
  userName,
}: PTORequestModalProps) {
  const { t, i18n } = useTranslation();

  const dayToStr = (d: Date) => format(d, "yyyy-MM-dd");

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-2 md:tw-p-4">
      <div className="tw-w-full tw-max-w-3xl tw-bg-white tw-rounded-lg tw-border tw-border-[#E5E7EB] tw-shadow-xl tw-max-h-[90vh] tw-flex tw-flex-col">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b">
          <h2 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
            {requestType === "vacation" ? (
              <LuFileClock className="tw-text-primary-light" size={24} />
            ) : (
              <FaBriefcaseMedical className="tw-text-primary-light" size={24} />
            )}
            <span>
              {t(
                `pto.create${
                  requestType.charAt(0).toUpperCase() + requestType.slice(1)
                }Requests`
              )}
              {userName && (
                <span className="tw-font-normal"> - {userName}</span>
              )}
            </span>
          </h2>
          <button
            onClick={onClose}
            className="tw-text-[#6B7280] hover:tw-text-black"
            aria-label="Close"
            title="Close"
            disabled={isPending}
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        <div className="tw-p-4 tw-space-y-4 tw-flex-1 tw-overflow-auto">
          <div className="tw-flex tw-items-center tw-justify-between">
            <button
              onClick={onPrevMonth}
              className="tw-p-2 tw-border tw-rounded-md"
              aria-label="Previous month"
              disabled={isPending}
            >
              <ChevronLeft className="tw-w-4 tw-h-4" />
            </button>
            <div className="tw-font-medium">
              {format(currentMonth, "MMMM yyyy", {
                locale: i18n.language === "es" ? es : enUS,
              })}
            </div>
            <button
              onClick={onNextMonth}
              className="tw-p-2 tw-border tw-rounded-md"
              aria-label="Next month"
              disabled={isPending}
            >
              <ChevronRight className="tw-w-4 tw-h-4" />
            </button>
          </div>

          <div className="tw-grid tw-grid-cols-7 tw-text-center tw-text-xs tw-text-[#6B7280]">
            {[
              t("pto.sun"),
              t("pto.mon"),
              t("pto.tue"),
              t("pto.wed"),
              t("pto.thu"),
              t("pto.fri"),
              t("pto.sat"),
            ].map((d) => (
              <div key={d} className="tw-py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="tw-grid tw-grid-cols-7 tw-gap-1">
            {monthMatrix.map((week, wi) => (
              <div key={wi} className="tw-contents">
                {week.map((day, di) => {
                  const dateStr = dayToStr(day);
                  const part = selection[dateStr];
                  const isCurrent = isSameMonth(day, currentMonth);
                  const baseCls = isCurrent ? "tw-bg-white" : "tw-bg-[#F3F4F6]";
                  const fullSel = part === "FULL";
                  const amSel = part === "AM";
                  const pmSel = part === "PM";

                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  // For sick leave, allow past days from the previous Sun–Sat pay period
                  // through the current pay period (so yesterday is always selectable)
                  const prevPeriodStart = startOfWeek(subWeeks(today, 1), {
                    weekStartsOn: 0,
                  });
                  const inAllowedSickPeriod =
                    requestType === "sick" && day >= prevPeriodStart;
                  const isPastDay = day < today && !inAllowedSickPeriod;

                  const blocked = blockedMap[dateStr] || {};
                  const amBlocked = !!(blocked.FULL || blocked.AM) || isPastDay;
                  const pmBlocked = !!(blocked.FULL || blocked.PM) || isPastDay;
                  const fullBlocked =
                    !!(blocked.FULL || (blocked.AM && blocked.PM)) || isPastDay;
                  const halfBlocked = !fullBlocked && (amBlocked || pmBlocked);

                  return (
                    <div
                      key={di}
                      className={`tw-h-16 tw-w-16 tw-rounded-full ${
                        fullBlocked ? "tw-bg-[#f8ecec]" : `${baseCls}`
                      } tw-relative tw-mx-auto tw-overflow-hidden`}
                    >
                      <button
                        type="button"
                        aria-label="Select first half of day"
                        onClick={() => {
                          if (!amBlocked) {
                            onToggle(dateStr);
                          }
                        }}
                        disabled={amBlocked || isPending}
                        className={`tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-1/2 ${
                          amBlocked
                            ? fullBlocked
                              ? "tw-cursor-not-allowed"
                              : "tw-cursor-not-allowed"
                            : amSel || fullSel
                            ? "tw-bg-blue-600"
                            : "hover:tw-bg-[#F3F4F6]"
                        }`}
                        style={{ zIndex: 1 }}
                      />
                      <button
                        type="button"
                        aria-label="Select second half of day"
                        onClick={() => {
                          if (!pmBlocked) {
                            onToggle(dateStr);
                          }
                        }}
                        disabled={pmBlocked || isPending}
                        className={`tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-h-1/2 ${
                          pmBlocked
                            ? fullBlocked
                              ? "tw-cursor-not-allowed"
                              : "tw-cursor-not-allowed"
                            : pmSel || fullSel
                            ? "tw-bg-blue-600"
                            : "hover:tw-bg-[#F3F4F6]"
                        }`}
                        style={{ zIndex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!amBlocked && !pmBlocked) {
                            onToggle(dateStr);
                          }
                        }}
                        disabled={amBlocked || pmBlocked || isPending}
                        className={`tw-absolute tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-transparent ${
                          fullSel
                            ? "tw-bg-blue-600 tw-text-white"
                            : amSel || pmSel
                            ? "tw-bg-[#DBEAFE] tw-text-[#111827]"
                            : "tw-bg-transparent tw-text-[#111827]"
                        }`}
                        style={{
                          width: "14px",
                          height: "14px",
                          zIndex: 2,
                          transform: "translate(-50%, -50%)",
                        }}
                        aria-disabled={amBlocked || pmBlocked || isPending}
                      >
                        <span
                          aria-hidden
                          className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-1/2 -tw-translate-y-1/2 tw-h-px tw-bg-transparent"
                        />
                        <span
                          className={`tw-text-[13px] tw-font-semibold tw-leading-none ${
                            fullSel ? "tw-text-white" : ""
                          }`}
                        >
                          {format(day, "d")}
                        </span>
                        {halfBlocked && (
                          <span
                            aria-hidden
                            className="tw-absolute tw-w-1.5 tw-h-1.5 tw-bg-[#DC2626] tw-rounded-full -tw-top-0.5 -tw-right-0.5 tw-border tw-border-white"
                          />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="tw-space-y-1">
            <label className="tw-text-sm tw-text-[#374151]">
              {t("pto.descriptionOptional")}
            </label>
            <textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className="tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#3B82F6]"
              rows={3}
              placeholder={t("pto.descriptionPlaceholder")}
              disabled={isPending}
            />
          </div>

          {submitError && (
            <div className="tw-text-sm tw-text-red-600">{submitError}</div>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-3 tw-border-t tw-bg-[#F9FAFB]">
          <button
            onClick={onClose}
            className="tw-px-3 tw-py-2 tw-rounded-md tw-bg-gray-100 tw-text-gray-800 hover:tw-bg-gray-200 disabled:tw-opacity-60"
            disabled={isPending}
            aria-label="Cancel"
            title="Cancel"
          >
            <span className="tw-inline-flex tw-items-center tw-gap-2">
              <XCircle className="tw-w-4 tw-h-4" />
              <span>{t("pto.cancel")}</span>
            </span>
          </button>
          <button
            onClick={onFinalize}
            className={`tw-px-3 tw-py-2 tw-rounded-md tw-text-white ${
              showSuccessAnim
                ? "tw-bg-green-600"
                : "tw-bg-blue-600 hover:tw-bg-blue-700"
            }disabled:tw-opacity-60`}
            disabled={isPending || showSuccessAnim}
          >
            <span className="tw-inline-flex tw-items-center tw-gap-2">
              {isPending ? (
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
              ) : showSuccessAnim ? (
                <CheckCircle className="tw-w-4 tw-h-4 tw-pulse-check" />
              ) : (
                <CheckCircle className="tw-w-4 tw-h-4" />
              )}
              <span>
                {isPending
                  ? t("pto.submitting")
                  : showSuccessAnim
                  ? t("pto.submitted")
                  : t("pto.finalizeRequests")}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
